import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LecturerSidebar from '../components/LecturerSidebar';
import axios from '../services/api';

const LecturerDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [timetable, setTimetable] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchTimetable = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/timetables/lecturer/${user.id}`);
        setTimetable(response.data);
      } catch (error) {
        console.error('Error fetching timetable:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user?.id) {
      fetchTimetable();
    }
  }, [user?.id]);

  const formattedTime = currentTime.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).split(' ');

  const formattedDate = currentTime.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  const renderTimetable = () => {
    if (loading) {
      return <div>Loading timetable...</div>;
    }

    if (!timetable.length) {
      return <div>This feature will be available in the next update.</div>;
    }

    return (
      <div style={{ overflowX: 'auto' }}>
        <table style={{
          width: '100%',
          borderCollapse: 'separate',
          borderSpacing: '0 8px',
          margin: '20px 0'
        }}>
          <thead>
            <tr>
              <th style={tableHeaderStyle}>Day</th>
              <th style={tableHeaderStyle}>Time</th>
              <th style={tableHeaderStyle}>Module</th>
              <th style={tableHeaderStyle}>Location</th>
              <th style={tableHeaderStyle}>Type</th>
            </tr>
          </thead>
          <tbody>
            {timetable.map((session, index) => (
              <tr key={index} style={tableRowStyle}>
                <td style={tableCellStyle}>{session.day}</td>
                <td style={tableCellStyle}>
                  {session.start_time} - {session.end_time}
                </td>
                <td style={tableCellStyle}>
                  {session.module_code}<br />
                  <span style={{ fontSize: '12px', color: '#6c757d' }}>
                    {session.module_name}
                  </span>
                </td>
                <td style={tableCellStyle}>{session.location}</td>
                <td style={tableCellStyle}>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    backgroundColor: session.session_type === 'lecture' ? '#e9ecef' : '#f8f9fa',
                    color: '#495057'
                  }}>
                    {session.session_type}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Table styles
  const tableHeaderStyle = {
    textAlign: 'left',
    padding: '12px',
    color: '#495057',
    fontWeight: 600,
    borderBottom: '2px solid #dee2e6'
  };

  const tableRowStyle = {
    backgroundColor: 'white',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
    transition: 'transform 0.2s',
    ':hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
    }
  };

  const tableCellStyle = {
    padding: '16px',
    borderBottom: '1px solid #dee2e6'
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#F8F9FA' }}>
      <LecturerSidebar />

      {/* Main Content */}
      <div style={{ 
        marginLeft: window.innerWidth <= 768 ? '0' : '290px', 
        padding: window.innerWidth <= 480 ? '60px 15px 20px 15px' : window.innerWidth <= 768 ? '60px 20px 20px 20px' : '40px', 
        flex: 1, 
        maxWidth: window.innerWidth <= 768 ? '100vw' : 'calc(100vw - 290px)',
        transition: 'margin-left 0.3s ease'
      }}>
        
        {/* Welcome Section with Modern Design */}
        <div style={{
          background: 'linear-gradient(135deg,  #dc6a35f3 0%, #fd7e14 100%)',
          borderRadius: window.innerWidth <= 480 ? 12 : 20,
          padding: window.innerWidth <= 480 ? '24px' : window.innerWidth <= 768 ? '32px' : '40px',
          marginBottom: window.innerWidth <= 480 ? 24 : 40,
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 10px 40px rgba(102, 126, 234, 0.3)'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: window.innerWidth <= 768 ? '200px' : '300px',
            height: '100%',
            background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            zIndex: 1
          }}></div>
          
          <div style={{ position: 'relative', zIndex: 2 }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              marginBottom: 16,
              flexDirection: window.innerWidth <= 480 ? 'column' : 'row',
              textAlign: window.innerWidth <= 480 ? 'center' : 'left'
            }}>
              <div style={{
                width: window.innerWidth <= 480 ? 50 : 60,
                height: window.innerWidth <= 480 ? 50 : 60,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: window.innerWidth <= 480 ? 0 : 20,
                marginBottom: window.innerWidth <= 480 ? 12 : 0,
                fontSize: window.innerWidth <= 480 ? 20 : 24,
                fontWeight: 'bold'
              }}>
                {user?.name ? user.name.charAt(0).toUpperCase() : 'L'}
              </div>
              <div>
                <h1 style={{ 
                  fontSize: window.innerWidth <= 480 ? 24 : window.innerWidth <= 768 ? 28 : 32, 
                  fontWeight: 700, 
                  margin: 0, 
                  fontFamily: 'Outfit',
                  textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  lineHeight: '1.2'
                }}>
                  Welcome back, {user?.name || 'Lecturer'}
                </h1>
                <div style={{
                  fontSize: window.innerWidth <= 480 ? 12 : 14,
                  opacity: 0.9,
                  paddingRight: window.innerWidth <= 480 ? '0' : '150px',
                  marginTop: 4,
                  fontFamily: 'Outfit'
                }}>
                  {formattedDate} • {formattedTime[0]} {formattedTime[1]}
                </div>
              </div>
            </div>
            
            <p style={{ 
              fontSize: window.innerWidth <= 480 ? 14 : 16, 
              opacity: 0.9,
              maxWidth: window.innerWidth <= 480 ? '100%' : 600,
              fontFamily: 'Outfit',
              lineHeight: 1.5,
              paddingRight: window.innerWidth <= 480 ? '0' : '180px',
              textAlign: window.innerWidth <= 480 ? 'center' : 'left',
              margin: window.innerWidth <= 480 ? '0 auto' : '0'
            }}>
              Manage your classes and take attendance efficiently.
            </p>
          </div>
        </div>

        {/* Timetable Section */}
        <div style={{
          background: 'white',
          borderRadius: window.innerWidth <= 480 ? 12 : 20,
          padding: window.innerWidth <= 480 ? '20px' : '32px',
          marginTop: window.innerWidth <= 480 ? '20px' : '40px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.06)'
        }}>
          <h2 style={{ 
            fontSize: window.innerWidth <= 480 ? 20 : 24, 
            fontWeight: 600, 
            color: '#2C3E50',
            marginBottom: window.innerWidth <= 480 ? 16 : 24
          }}>
            Your Class Schedule
          </h2>
          {renderTimetable()}
        </div>

        {/* Action Center */}
        <div style={{
          background: 'white',
          borderRadius: window.innerWidth <= 480 ? 12 : 20,
          padding: window.innerWidth <= 480 ? '24px' : '40px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
          border: '1px solid #E9ECEF',
          textAlign: 'center',
          marginTop: window.innerWidth <= 480 ? '20px' : '40px'
        }}>
          <div style={{ marginBottom: window.innerWidth <= 480 ? 24 : 32 }}>
            <h2 style={{ 
              fontSize: window.innerWidth <= 480 ? 20 : 24, 
              fontWeight: 600, 
              color: '#2C3E50', 
              marginBottom: 8,
              fontFamily: 'Outfit'
            }}>
              Quick Actions
            </h2>
            <p style={{ 
              fontSize: window.innerWidth <= 480 ? 14 : 16, 
              color: '#6C757D', 
              fontFamily: 'Outfit',
              margin: 0
            }}>
              Manage your classes and take attendance efficiently
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: window.innerWidth <= 480 ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: window.innerWidth <= 480 ? 16 : 24,
            maxWidth: 800,
            margin: '0 auto'
          }}>
            
            {/* Take Attendance Card */}
            <div 
              onClick={() => navigate('/qr')}
              style={{
                background: 'linear-gradient(135deg, #F04915 0%, #FF8C42 100%)',
                borderRadius: window.innerWidth <= 480 ? 12 : 16,
                padding: window.innerWidth <= 480 ? '24px' : '32px',
                color: 'white',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(240, 73, 21, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(240, 73, 21, 0.2)';
              }}
            >
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: window.innerWidth <= 480 ? '80px' : '100px',
                height: window.innerWidth <= 480 ? '80px' : '100px',
                background: 'rgba(255,255,255,0.1)',
                borderRadius: '50%',
                transform: window.innerWidth <= 480 ? 'translate(25px, -25px)' : 'translate(30px, -30px)'
              }}></div>
              
              <div style={{ position: 'relative', zIndex: 2 }}>
                <div style={{
                  width: window.innerWidth <= 480 ? 48 : 56,
                  height: window.innerWidth <= 480 ? 48 : 56,
                  borderRadius: window.innerWidth <= 480 ? 12 : 14,
                  background: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: window.innerWidth <= 480 ? '0 auto 16px' : '0 auto 20px'
                }}>
                  <svg width={window.innerWidth <= 480 ? "24" : "28"} height={window.innerWidth <= 480 ? "24" : "28"} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" fill="white"/>
                  </svg>
                </div>
                
                <h3 style={{ 
                  fontSize: window.innerWidth <= 480 ? 18 : 20, 
                  fontWeight: 600, 
                  marginBottom: 8,
                  fontFamily: 'Outfit'
                }}>
                  Take Attendance
                </h3>
                <p style={{ 
                  fontSize: window.innerWidth <= 480 ? 12 : 14, 
                  opacity: 0.9,
                  margin: 0,
                  fontFamily: 'Outfit'
                }}>
                  Generate QR code for students to mark their attendance
                </p>
              </div>
            </div>

            {/* View Records Card */}
            <div 
              onClick={() => navigate('/lecturer/students')}
              style={{
                background: 'white',
                border: '2px solid #E9ECEF',
                borderRadius: window.innerWidth <= 480 ? 12 : 16,
                padding: window.innerWidth <= 480 ? '24px' : '32px',
                color: '#2C3E50',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                position: 'relative'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.borderColor = '#F04915';
                e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.12)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = '#E9ECEF';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{
                width: window.innerWidth <= 480 ? 48 : 56,
                height: window.innerWidth <= 480 ? 48 : 56,
                borderRadius: window.innerWidth <= 480 ? 12 : 14,
                background: '#F8F9FA',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: window.innerWidth <= 480 ? '0 auto 16px' : '0 auto 20px'
              }}>
                <svg width={window.innerWidth <= 480 ? "24" : "28"} height={window.innerWidth <= 480 ? "24" : "28"} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19ZM7 10H17V12H7V10ZM7 14H17V16H7V14ZM7 6H17V8H7V6Z" fill="#F04915"/>
                </svg>
              </div>
              
              <h3 style={{ 
                fontSize: window.innerWidth <= 480 ? 18 : 20, 
                fontWeight: 600, 
                marginBottom: 8,
                fontFamily: 'Outfit'
              }}>
                View Students
              </h3>
              <p style={{ 
                fontSize: window.innerWidth <= 480 ? 12 : 14, 
                color: '#6C757D',
                margin: 0,
                fontFamily: 'Outfit'
              }}>
                Browse and manage your student records
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LecturerDashboard;